import { Reducer, useEffect, useReducer } from "react"
import { Club, TrendingClubList } from "../book/data-types"

import { useAuth } from "./auth"
import { fetchClubMembers, fetchTrendingClubs, getClubById } from "../book/club"
import { ChatEntityType } from "../messaging/data-types"
import { getGeneralChatById } from "../general_chat/general-chat"
import { GeneralChat } from "../general_chat/data-types"
import { User } from "../auth/data-types"
import { useReadingLanguages } from "./feed"


type ClubState = {
    pending: boolean
    data?: Club
    error?: string
}

type UserListState = {
    pending: boolean
    data?: User[]
    error?: string
}

type TrendingClubsState = {
    pending: boolean
    data?: TrendingClubList
    error?: string
}

type EntityState = {
    pending: boolean
    data?: Club | GeneralChat
    error?: string
}

export const useEntity = (entity: ChatEntityType, entityId: string) => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<EntityState, Partial<EntityState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            data: undefined,
        }
    )

    useEffect(() => {
        const f = async () => {
            setState({ pending: true })
            try {
                switch (entity) {
                    case ChatEntityType.Club: {
                        const club = await getClubById(entityId, user.token?.access!)
                        setState({ pending: false, data: club })
                        break
                    }
                    case ChatEntityType.GeneralChat: {
                        const gc = await getGeneralChatById(entityId, user.token?.access!)
                        setState({ pending: false, data: gc })
                        break
                    }
                }
            } catch (e) {
                console.log(`Error getting club: ${e}`)
                setState({ pending: false, error: "Error getting club"})
            }
        }
        f()
    }, [entity, entityId, user.token])

    return state
}

export const useClub = (clubId: string): ClubState => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<ClubState, Partial<ClubState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            data: undefined,
        }
    )

    useEffect(() => {
        const f = async () => {
            setState({ pending: true })
            try {
                const club = await getClubById(clubId, user.token?.access!)
                setState({ pending: false, data: club })
            } catch (e) {
                console.log(`Error getting club: ${e}`)
                setState({ pending: false, error: "Error getting club"})
            }
        }
        f()
    }, [clubId, user.token])

    return state
}

export const useClubMembers = (clubId: number): [UserListState, (offset: number) => void] => {
    const user = useAuth()
    const [userLanguages, _] = useReadingLanguages()

    const [state, setState] = useReducer<Reducer<UserListState, Partial<UserListState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            data: undefined,
        }
    )

    const update = async (offset: number, force?: boolean) => {
        setState({ pending: true, data: force ? [] : state.data })
        try {
            const users = await fetchClubMembers(clubId, offset, 20, userLanguages.data, user.token?.access)
            setState({ pending: false, data: [...( force ? [] : state.data || []), ...users] })
        } catch (e) {
            console.log(`Error fetching club members: ${e}`)
            setState({ pending: false, error: "Error fetching club members"})
        }
    }

    useEffect(() => {
        update(0, true)
    }, [user.token, clubId, userLanguages.data])

    return [state, update]
}

export const useTrendingClubs = (): TrendingClubsState => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<TrendingClubsState, Partial<TrendingClubsState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            data: undefined,
        }
    )

    useEffect(() => {
        (async () => {

            setState({ pending: true, data: undefined })
            try {
                const trendingClubs = await fetchTrendingClubs(user.token?.access)
                setState({ pending: false, data: trendingClubs })
            } catch (e) {
                console.log(`Error fetching trending clubs: ${e}`)
                setState({ pending: false, error: "Error fetching trending clubs"})
            }
        })()
    }, [user.token])

    return state
}

